import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { isAdminRole } from '../lib/roles';
import { buildApiUrl, fetchJson } from '../lib/api';

const emptyRegister = {
  email: '',
  password: '',
  nombre: '',
  apellido: '',
  documentoTipo: 'CUIT',
  documento: '',
  razonSocial: '',
  domicilio: '',
  codigoPostal: '',
  telefono: '',
  tipoFactura: '',
  condicionIva: '',
  aceptaTerminos: false,
};

function resolvePostAuthPath(data, user) {
  const rol = data?.rol ?? user?.rol;
  const clienteId = data?.clienteId ?? user?.clienteId;
  const tiendaNubeId = data?.tiendaNubeId ?? user?.tiendaNubeId;

  if (isAdminRole(rol)) {
    return '/admin';
  }
  if (!clienteId) {
    return '/perfil?setup=1';
  }
  if (!tiendaNubeId) {
    return '/conectar-tienda';
  }
  return '/portal';
}

export default function Login() {
  const { login, register, user, isAuthenticated, authReady } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const installToken = searchParams.get('installToken');
  const accountDeleted = searchParams.get('accountDeleted') === '1';
  const [mode, setMode] = useState(() => {
    const fromUrl = searchParams.get('mode');
    return fromUrl === 'register' ? 'register' : 'login';
  });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState(emptyRegister);
  const [condicionesIva, setCondicionesIva] = useState([]);
  const [tiposFactura, setTiposFactura] = useState([]);
  const [catalogosLoading, setCatalogosLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (installToken) {
      const lang = searchParams.get('lang') || 'es';
      window.location.href = buildApiUrl(
        `/auth/tiendanube/install?token=${encodeURIComponent(installToken)}&lang=${encodeURIComponent(lang)}`,
      );
    }
  }, [installToken, searchParams]);

  useEffect(() => {
    if (mode !== 'register') {
      return;
    }

    let cancelled = false;
    setCatalogosLoading(true);

    const load = async () => {
      try {
        const [ivaRes, factRes] = await Promise.all([
          fetchJson('/catalogos/condiciones-iva'),
          fetchJson('/catalogos/tipos-factura'),
        ]);

        if (!cancelled) {
          setCondicionesIva(Array.isArray(ivaRes?.data) ? ivaRes.data : []);
          setTiposFactura(Array.isArray(factRes?.data) ? factRes.data : []);
        }
      } catch {
        if (!cancelled) {
          setCondicionesIva([
            { id: 1, nombre: 'Responsable Inscripto' },
            { id: 2, nombre: 'Monotributista' },
            { id: 3, nombre: 'Exento' },
            { id: 4, nombre: 'Consumidor Final' },
          ]);
          setTiposFactura([
            { id: 1, nombre: 'Factura A' },
            { id: 2, nombre: 'Factura B' },
            { id: 3, nombre: 'Factura C' },
            { id: 4, nombre: 'Factura E' },
          ]);
        }
      } finally {
        if (!cancelled) {
          setCatalogosLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await login(loginForm.email, loginForm.password, installToken);
      navigate(resolvePostAuthPath(data, null), { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const documentoTipo = registerForm.documentoTipo;
      const documento = registerForm.documento.trim();
      const data = await register({
        email: registerForm.email,
        password: registerForm.password,
        nombre: registerForm.nombre,
        apellido: registerForm.apellido,
        documentoTipo,
        documento,
        cuit: documento,
        esEmpresa: true,
        razonSocial: registerForm.razonSocial,
        domicilio: registerForm.domicilio,
        codigoPostal: registerForm.codigoPostal,
        telefono: registerForm.telefono,
        tipoFactura: Number(registerForm.tipoFactura),
        condicionIva: Number(registerForm.condicionIva),
        condicionPago: 'debito_automatico',
        aceptaTerminos: registerForm.aceptaTerminos,
      }, installToken);

      navigate(resolvePostAuthPath(data, null), { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (authReady && isAuthenticated) {
    return <Navigate to={resolvePostAuthPath(null, user)} replace />;
  }

  return (
    <div className="page-shell">
      <section className="hero-panel hero-panel--home">
        <div className="hero-copy">
          <p className="eyebrow">Acceso y alta</p>
          <h1>Ingresar o darte de alta como cliente</h1>
          <p className="hero-text">
            Para registrarte pedimos los datos de facturación completos, aceptación de políticas y un documento
            válido. Luego accedés al portal con tus pedidos y tracking.
          </p>
          <div className="hero-tags">
            <span>Alta obligatoria</span>
            <span>Políticas y términos</span>
            <span>Portal cliente</span>
          </div>
        </div>

        <div className="hero-side">
          <div className="panel quick-panel">
            <p className="eyebrow">Pasos</p>
            <div className="admin-links">
              <div className="link-card">
                <strong>1. Registrarte</strong>
                <span>Completa datos fiscales y acepta los términos.</span>
              </div>
              <div className="link-card">
                <strong>2. Crear cuenta</strong>
                <span>Se crea tu usuario, cliente y sesión.</span>
              </div>
              <div className="link-card">
                <strong>3. Conectar tienda</strong>
                <span>Autorizás la app de Tiendanube para asociar tu tienda.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-grid">
        <article className="panel auth-panel">
          <div className="tabs-strip">
            <button
              type="button"
              className={`tab-pill ${mode === 'login' ? 'active' : ''}`}
              onClick={() => setMode('login')}
            >
              Ingresar
            </button>
            <button
              type="button"
              className={`tab-pill ${mode === 'register' ? 'active' : ''}`}
              onClick={() => setMode('register')}
            >
              Registrarme
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <label>
                Email
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
                  required
                />
              </label>
              <label>
                Contraseña
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                  required
                />
              </label>
              <div className="login-footer">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Ingresando...' : 'Entrar'}
                </button>
                <button type="button" onClick={() => navigate('/forgot-password')} className="forgot-password-link">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-section">
                <h3>Cuenta</h3>
                <div className="grid-2">
                  <label>
                    Email
                    <input
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Contraseña
                    <input
                      type="password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, password: e.target.value }))}
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h3>Datos de contacto</h3>
                <div className="grid-2">
                  <label>
                    Nombre
                    <input
                      value={registerForm.nombre}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, nombre: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Apellido
                    <input
                      value={registerForm.apellido}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, apellido: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Teléfono
                    <input
                      value={registerForm.telefono}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, telefono: e.target.value }))}
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="form-section">
                <h3>Facturación</h3>
                <div className="grid-2">
                  <label>
                    Tipo de documento
                    <select
                      value={registerForm.documentoTipo}
                      onChange={(e) =>
                        setRegisterForm((prev) => ({ ...prev, documentoTipo: e.target.value, documento: '' }))
                      }
                      required
                    >
                      <option value="CUIT">CUIT / CUIL</option>
                      <option value="DNI">DNI</option>
                    </select>
                  </label>
                  <label>
                    {registerForm.documentoTipo === 'DNI' ? 'DNI' : 'CUIT / CUIL'}
                    <input
                      type="text"
                      value={registerForm.documento}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, documento: e.target.value }))}
                      placeholder={registerForm.documentoTipo === 'DNI' ? '12345678' : '20-12345678-3'}
                      pattern={registerForm.documentoTipo === 'DNI' ? '\\d{7,8}' : '\\d{2}-\\d{8}-\\d'}
                      required
                    />
                  </label>
                  <label>
                    Razón social
                    <input
                      value={registerForm.razonSocial}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, razonSocial: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Domicilio
                    <input
                      value={registerForm.domicilio}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, domicilio: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Código postal
                    <input
                      value={registerForm.codigoPostal}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, codigoPostal: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    Condición frente al IVA
                    <select
                      value={registerForm.condicionIva}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, condicionIva: e.target.value }))}
                      required
                      disabled={catalogosLoading}
                    >
                      <option value="">{catalogosLoading ? 'Cargando...' : 'Seleccionar'}</option>
                      {condicionesIva.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Tipo de factura
                    <select
                      value={registerForm.tipoFactura}
                      onChange={(e) => setRegisterForm((prev) => ({ ...prev, tipoFactura: e.target.value }))}
                      required
                      disabled={catalogosLoading}
                    >
                      <option value="">{catalogosLoading ? 'Cargando...' : 'Seleccionar'}</option>
                      {tiposFactura.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nombre}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <section className="form-section">
                <h3>Políticas y condiciones</h3>
                <div className="selection-chip">
                  <strong>Obligatorio</strong>
                  <span>Debes aceptar políticas de uso, privacidad y facturación para crear la cuenta.</span>
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={registerForm.aceptaTerminos}
                    onChange={(e) => setRegisterForm((prev) => ({ ...prev, aceptaTerminos: e.target.checked }))}
                    required
                  />
                  Acepto los términos, condiciones y políticas de facturación.
                </label>
              </section>

              <button type="submit" className="btn-primary" disabled={loading || catalogosLoading}>
                {loading ? 'Registrando...' : 'Crear cuenta'}
              </button>
            </form>
          )}

          {accountDeleted ? <div className="portal-alert portal-alert--success">La cuenta fue desactivada correctamente.</div> : null}
          {error && <div className="alert error">{error}</div>}
        </article>

        <aside className="panel quick-panel">
          <p className="eyebrow">Resumen</p>
          <h2>Control de acceso</h2>
          <ul className="feature-list">
            <li>El alta genera usuario y cliente en la base.</li>
            <li>Los datos fiscales son obligatorios.</li>
            <li>La aceptación de términos queda registrada.</li>
            <li>Si el email o documento ya existen, se devuelve un error.</li>
            <li>La condición de pago por defecto es débito automático.</li>
          </ul>
        </aside>
      </section>
    </div>
  );
}
